import type { IslandPart } from '../../api/models';
import { AppText } from '../../components/AppText';
import { RetryNotice } from '../../components/RetryNotice';
import { Strings } from '../../strings';
import { SymbolView } from '../../symbols/SymbolView';

import { useIsland } from './useIsland';

/**
 * design-system/09-Rewards-And-Island.md.
 * Mirrors android/…/Screens/Island/IslandScreen.kt.
 *
 * The three booleans the API sends are three different states and are never collapsed into one: a part can
 * be affordable and still not this child's turn. A locked part is dimmed, never crossed out, and never
 * described as something the child failed to get.
 */
export function IslandScreen() {
  const model = useIsland();
  const island = model.state.island;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <AppText as="h1" style="titleL">
        {Strings.childHomeIsland}
      </AppText>

      <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
        <AppText as="span" style="label" color="var(--reward-coin)">
          {Strings.childCoins}: {island?.coinBalance ?? 0}
        </AppText>
        <AppText as="span" style="label" color="var(--reward-star)">
          {Strings.childStars}: {island?.starBalance ?? 0}
        </AppText>
      </div>

      {model.state.error !== null ? (
        <RetryNotice text={model.state.error} tone="info" onRetry={() => void model.load()} />
      ) : null}

      {(island?.parts ?? []).map((part: IslandPart) => (
        <PartRow
          key={part.code}
          coinBalance={island?.coinBalance ?? 0}
          coinsPerSession={island?.coinsPerCompletedSession ?? 0}
          part={part}
          isUnlocking={model.state.unlockingCode === part.code}
          onUnlock={() => void model.unlock(part.code)}
        />
      ))}
    </div>
  );
}

function PartRow({
  part,
  coinBalance,
  coinsPerSession,
  isUnlocking,
  onUnlock,
}: {
  part: IslandPart;
  coinBalance: number;
  coinsPerSession: number;
  isUnlocking: boolean;
  onUnlock: () => void;
}) {
  const canUnlock = !part.isUnlocked && part.isAvailable && part.isAffordable && !isUnlocking;
  // Only the part the child is actually working towards explains itself. Showing a distance on all six
  // would be six numbers and no next step, which is the screen the audit found (R0078).
  const isNextStep = !part.isUnlocked && part.isAvailable && !part.isAffordable;
  const remaining = Math.max(part.costCoins - coinBalance, 0);

  return (
    <button
      type="button"
      disabled={!canUnlock}
      onClick={onUnlock}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        borderRadius: 'var(--radius-l)',
        background: part.isUnlocked ? 'var(--surface-reward)' : 'var(--surface-raised)',
        border: 'none',
        opacity: part.isUnlocked || part.isAvailable ? 1 : 0.55,
        padding: 'var(--space-5)',
        textAlign: 'left',
        cursor: canUnlock ? 'pointer' : 'default',
      }}
    >
      <SymbolView
        token={symbolFor(part.code)}
        size={40}
        color={part.isUnlocked ? 'var(--action-primary)' : 'var(--text-secondary)'}
      />

      <span style={{ flex: 1 }}>
        <AppText as="span" style="titleS">
          {part.name}
        </AppText>
        <AppText style="bodyS" color="var(--text-secondary)">
          {part.isUnlocked
            ? Strings.islandUnlocked
            : !part.isAvailable
              ? Strings.islandLocked
              : !part.isAffordable
                ? // The distance, not "you need more coins" — the number is the whole point (R0078).
                  Strings.islandStillNeed(String(remaining))
                : Strings.islandUnlock}
        </AppText>

        {isNextStep ? (
          <>
            <div
              aria-hidden="true"
              style={{
                height: 'var(--space-2)',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--surface-sunken)',
                overflow: 'hidden',
                marginTop: 'var(--space-2)',
              }}
            >
              <div
                style={{
                  width: `${Math.min((coinBalance / part.costCoins) * 100, 100)}%`,
                  height: '100%',
                  background: 'var(--reward-coin)',
                }}
              />
            </div>
            {coinsPerSession > 0 ? (
              <AppText style="bodyS" color="var(--text-secondary)">
                {Strings.islandEarnPerSession(String(coinsPerSession))}
              </AppText>
            ) : null}
          </>
        ) : null}
      </span>

      <AppText as="span" style="numeric" color="var(--reward-coin)">
        {part.costCoins}
      </AppText>
    </button>
  );
}

/**
 * The island parts have no illustrations yet (R0008 Q-02), so each borrows the nearest symbol from the
 * contract rather than showing a blank tile. An unmapped code falls through to the placeholder, which is
 * visible and reported — the same rule as any unknown token.
 */
function symbolFor(partCode: string): string {
  switch (partCode) {
    case 'house':
      return 'house';
    case 'garden':
      return 'tree';
    case 'animals':
      return 'dog';
    case 'playground':
      return 'ball';
    case 'lake':
      return 'fish';
    case 'lighthouse':
      return 'flag';
    default:
      return partCode;
  }
}
